import React from 'react';
import {
  Alert,
  Box,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography
} from '@mui/material';

export type AndroidAdUnitTrialField =
  | 'androidAdUnitTrialEnabled'
  | 'androidAdUnitTrialPercentage'
  | 'androidAdUnitTrialInterstitialAdId'
  | 'androidAdUnitTrialRewardedAdId';

interface AndroidAdUnitTrialProps {
  enabled: boolean;
  percentage: number;
  interstitialAdId: string;
  rewardedAdId: string;
  /** Same rule as the app: v2 is used when both v2 interstitial ids are set. */
  usesV2: boolean;
  liveInterstitialAdId: string;
  liveRewardedAdId: string;
  /** Test mode of whichever unit set the app is using. */
  testModeOn: boolean;
  adsEnabled: boolean;
  onChange: (field: AndroidAdUnitTrialField, value: boolean | number | string) => void;
}

const clampPercentage = (raw: string): number => {
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, n));
};

/**
 * Alternate Android interstitial/rewarded units served to a percentage of
 * Android app launches. The server swaps them into GET /devices/ads/config
 * (see server/src/utils/androidAdUnitTrial.js).
 */
const AndroidAdUnitTrial: React.FC<AndroidAdUnitTrialProps> = ({
  enabled,
  percentage,
  interstitialAdId,
  rewardedAdId,
  usesV2,
  liveInterstitialAdId,
  liveRewardedAdId,
  testModeOn,
  adsEnabled,
  onChange
}) => {
  const targetLabel = usesV2
    ? 'the v2 units (New AdMob Account)'
    : 'the legacy Shared Ad Unit IDs';

  return (
    <Paper sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'secondary.main', bgcolor: 'background.default' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box>
          <Typography variant="h6" color="secondary">
            Android Ad Unit Trial
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Android only. iOS is never affected.
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => onChange('androidAdUnitTrialEnabled', e.target.checked)}
              color="secondary"
            />
          }
          label={enabled ? 'Enabled' : 'Disabled'}
        />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Serve a different Android interstitial and rewarded unit to a share of app launches.
        Everyone else keeps the live units. Turn it off and every device goes back to the live
        units the next time the app starts.
      </Typography>

      {enabled && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            The trial currently replaces <strong>{targetLabel}</strong>. Create the trial units in
            the same AdMob app as those units: the App ID is built into the Android app and can't
            change at runtime.
          </Alert>

          {!adsEnabled && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Ads are disabled, so the trial has no effect until ads are enabled.
            </Alert>
          )}
          {adsEnabled && testModeOn && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Test mode is on for these units, so the app shows Google test ads and the trial has
              no effect.
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Share of Android App Launches (%)"
                value={percentage}
                onChange={(e) => onChange('androidAdUnitTrialPercentage', clampPercentage(e.target.value))}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                helperText="0 = nobody. 2 = about 2% of launches. Decimals allowed."
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Trial Android Interstitial Ad ID"
                value={interstitialAdId}
                onChange={(e) => onChange('androidAdUnitTrialInterstitialAdId', e.target.value)}
                placeholder="Blank = keep the live unit"
                helperText={`Live: ${liveInterstitialAdId || 'not set'}`}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Trial Android Rewarded Ad ID"
                value={rewardedAdId}
                onChange={(e) => onChange('androidAdUnitTrialRewardedAdId', e.target.value)}
                placeholder="Blank = keep the live unit"
                helperText={`Live: ${liveRewardedAdId || 'not set'}`}
              />
            </Grid>
          </Grid>

          <Typography variant="body2" color="text.secondary">
            The current app doesn't send a device ID when it loads ad settings, so each app launch
            is drawn separately: the same user can get trial units on one launch and live units on
            the next. App builds that send an X-Device-ID header stay on one set for the whole UTC
            day. Compare results per ad unit in AdMob reports.
          </Typography>
        </>
      )}
    </Paper>
  );
};

export default AndroidAdUnitTrial;
